import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { useRouter } from "@/app/hooks/useRouter";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import travelIcon from "@/assets/images/travel.svg";
import { formatAmount } from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/utils/confirmationHelpers";

interface BillingSummary {
  totalPrice: number;
  totalDiscount: number;
  totalAmountToPay: number;
  amountToPayAtClinic: number;
  amountToPayNow: number;
}

interface PricingSummaryCardProps {
  billingSummary: BillingSummary;
  isBookingFeeWaived: boolean;
  advancePaymentAmount: number;
  isPaidBooking: boolean;
  completedBookingFlow: boolean;
  bookingFeeChip: any;
  bookingFeeChipText: any;
  isHomeServiceBooking: boolean;
  selectedAddressLabel: string;
  wasLastVisitAddress: boolean;
  selectedVisitAddress: string;
  estimatedTravelTimeLabel: string;
  availableSlotsAddressChangeUrl: string;
  disableBookingCreationButton: boolean;
  rescheduleFlag: boolean;
  customerId: string;
  petId: string;
  logPreviewEvent: (event_type: string, extra?: any) => void;
  handelConfirmation: () => void;
}

const PricingSummaryCard = ({
  billingSummary,
  isBookingFeeWaived,
  advancePaymentAmount,
  isPaidBooking,
  completedBookingFlow,
  bookingFeeChip,
  bookingFeeChipText,
  isHomeServiceBooking,
  selectedAddressLabel,
  wasLastVisitAddress,
  selectedVisitAddress,
  estimatedTravelTimeLabel,
  availableSlotsAddressChangeUrl,
  disableBookingCreationButton,
  rescheduleFlag,
  customerId,
  petId,
  logPreviewEvent,
  handelConfirmation,
}: PricingSummaryCardProps) => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();

  return (
    <section className="rounded-[10px] border border-[#E4E7ED] bg-white p-3 shadow-[0_2px_10px_rgba(17,24,39,0.05)]">
      <h3 className="mb-3 text-[14px] font-semibold leading-6 text-[#222222]">Pricing Summary</h3>
      <div className="space-y-2 text-[12px]">
        <div className="flex items-center justify-between">
          <p className="text-[#6D6D6D] font-medium">Total Price</p>
          <p className="font-medium text-[#6D6D6D]">{formatAmount(billingSummary.totalPrice)}</p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[#6D6D6D] font-medium">Discount</p>
          <p className="font-medium text-[#239A3D]">{`-${formatAmount(billingSummary.totalDiscount)}`}</p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[#6D6D6D] font-medium">Advance Payment</p>
          {isBookingFeeWaived ? (
            <p className="font-medium">
              {advancePaymentAmount > 0 && (
                <span className="text-[#6D6D6D] line-through">{formatAmount(advancePaymentAmount)}</span>
              )}
              <span className="ml-2 text-[#239A3D]">FREE</span>
            </p>
          ) : (
            <p className="font-medium text-[#1F2431]">{formatAmount(advancePaymentAmount)}</p>
          )}
        </div>

        {isPaidBooking && !completedBookingFlow && (
          <p
            className="mt-2 inline-flex w-fit rounded-[4px] px-[4px] py-[2px] text-[10px]"
            style={{
              backgroundColor: bookingFeeChip?.bg,
              color: bookingFeeChip?.color,
            }}
          >
            {bookingFeeChipText}
          </p>
        )}
        {isHomeServiceBooking && (
          <div className="rounded-[8px] bg-[#F4F4F4] p-[8px]">
            <div className="flex items-center gap-2">
              <p className="text-[12px] font-medium text-[#000000]">Visit Address</p>
              <span className="rounded-[4px] bg-[#EEFFF5] px-[4px] py-[2px] text-[10px] font-medium text-[#00873C]">
                {selectedAddressLabel}
              </span>
              {wasLastVisitAddress && (
                <span className="rounded-[4px] bg-[#DEDEDE] px-[4px] py-[2px] text-[10px] font-medium text-[#666666]">
                  Last Visit
                </span>
              )}
            </div>
            <p className="mt-2 text-[12px] text-[#686E7C]">{selectedVisitAddress}</p>
            <div className="flex items-center mt-2 gap-2">
              <img src={travelIcon} alt="Time" className="inline-block h-3 w-3" />
              <p className=" text-[12px] text-[#6C6C6C]">
                Estimated travel time:{" "}
                <span className="text-[#000000] text-[12px] font-semibold">{estimatedTravelTimeLabel}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                logPreviewEvent("EDIT_ADDRESS_CLICK", { cta_text: "Change Address" });
                navigate(availableSlotsAddressChangeUrl);
              }}
              className="max-h-[36px] mt-2 h-[44px] w-full rounded-[6px] bg-white text-[12px] font-medium text-[#0068FF] py-[10px] px-[8px]"
            >
              Change Address
            </button>
          </div>
        )}
        <div className="border-t border-dashed border-[#EAEAEB] pt-2" />
        <div className="flex items-center justify-between">
          <p className="text-[12px] font-medium text-[#222222]">Total amount to pay</p>
          <p className="text-[12px] font-medium text-[#222222]">{formatAmount(billingSummary.totalAmountToPay)}</p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[#6D6D6D] font-medium">{isHomeServiceBooking ? "Pay at Visit" : "Pay at Clinic"}</p>
          <p className="font-medium text-[#6D6D6D]">{formatAmount(billingSummary.amountToPayAtClinic)}</p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[#6D6D6D] font-medium">Pay Now</p>
          <p className="font-medium text-[#222222]">{formatAmount(billingSummary.amountToPayNow)}</p>
        </div>
      </div>

      {!completedBookingFlow ? (
        <button
          type="button"
          onClick={() => handelConfirmation()}
          disabled={disableBookingCreationButton}
          className="mt-3 h-[37px] w-full rounded-[8px] bg-[#00873C] text-[14px] font-semibold text-white"
        >
          {disableBookingCreationButton ? (
            <div className="mx-auto w-fit">
              <CircleSpinnerLoader h="19" w="19" />
            </div>
          ) : rescheduleFlag ? (
            "Reschedule Booking"
          ) : isPaidBooking || billingSummary.amountToPayNow > 0 ? (
            "Pay & Confirm Booking"
          ) : (
            "Confirm Booking"
          )}
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <Link
            to={`${getRouteKey("APPOINTMENTS_TAB", "url")}/${customerId}/${petId}?source=slots`}
            className="h-[49px] block w-full rounded-[10px] bg-black px-2 text-center text-[14px] font-medium text-white leading-[49px]"
          >
            Book for Another Pet
          </Link>
          <Link
            to={getRouteKey("SEARCH_CUSTOMER", "url")}
            className="h-[49px] block w-full rounded-[10px] bg-white px-2 text-center text-[14px] font-medium text-black leading-[49px] border border-[#E2E2E2]"
          >
            Back To Home
          </Link>
        </div>
      )}
    </section>
  );
};

export default PricingSummaryCard;
